import React, { useCallback, useRef } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartArea,
  ScriptableContext,
  Color,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar, getElementAtEvent } from 'react-chartjs-2'
import dayjs from 'dayjs'
import { ContextData } from '@floating-ui/react-dom-interactions'
import { isPDFGenerationModeEnabled } from '../../lib/utils/isPDFGenerationModeEnabled'
import { match } from 'ts-pattern'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
)

const disableAnimations = isPDFGenerationModeEnabled()
  ? {
      animation: false,
    }
  : {}
const options = {
  ...disableAnimations,
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
    },
    crosshair: {
      line: {
        color: '#121124', // crosshair line color
        width: 1, // crosshair line width
      },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
  },
} as {
  animation?: false
  responsive: boolean
  maintainAspectRatio: boolean
  plugins: {
    legend: {
      display: boolean
      position?: 'bottom' | 'chartArea' | 'left' | 'top' | 'right' | 'center'
    }
    crosshair: {
      line: {
        color: string
        width: number
      }
    }
    tooltip: {
      mode?: 'index' | 'dataset' | 'point' | 'nearest' | 'x' | 'y'
      intersect: boolean
    }
  }
}

const getGreenToLemonGreenGradient = (
  ctx: ContextData,
  chartArea: ChartArea,
): ContextData => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  // #279661 0%, #8ECB5F 100%
  gradient.addColorStop(0, '#279661')
  gradient.addColorStop(1, '#8ECB5F')
  return gradient
}

const getBlueGradient = (ctx: ContextData, chartArea: ChartArea): ContextData => {
  // gradient from top to bottom
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  // #4776E6 with 0.9 opacity
  gradient.addColorStop(0, 'rgba(71, 118, 230, 0.9)')
  // #8E54E9 with 0.5 opacity
  gradient.addColorStop(0.8, 'rgba(142, 84, 233, 0.5)')
  // #837CD1 with 0.5 opacity
  gradient.addColorStop(1, 'rgba(131, 124, 209, 0.5)')
  return gradient
}

const gradients = {
  'green-to-lemon-green': getGreenToLemonGreenGradient,
  'blue-to-purple': getBlueGradient,
}

type PropsWithData = {
  type?: 'data'
  data: Array<{ label?: string; date?: string; value: number }>
}

type PropsWithDatasets = {
  type: 'dataset'
  labels: Array<string | number>
  datasets: Array<{
    label: string
    data: Array<{ label?: string; date?: string; value: number }>
    backgroundColor: string
  }>
}

type Props = {
  isLoading?: boolean
  isPreviousData?: boolean
  gradient?: keyof typeof gradients
  yKey?: string
  customOptions?: Record<string, unknown>
  onBarClick?: ({
    value,
    label,
    index,
  }: {
    value: number
    label: string
    index: number
  }) => void
} & (PropsWithData | PropsWithDatasets)

const BarChart = (props: Props): JSX.Element => {
  const {
    isLoading = false,
    isPreviousData = false,
    gradient = 'green-to-lemon-green',
    yKey,
    customOptions,
    onBarClick,
  } = props
  const chartRef = useRef(null)

  const handleClick = useCallback(
    (event) => {
      if (!chartRef.current) return

      const chart = chartRef.current as ChartJS

      const datasetIndex = getElementAtEvent(chart, event)[0]?.index

      if (datasetIndex !== -1) {
        const clickedData = chart.data.datasets[0].data[datasetIndex]
        const label = chart.data?.labels?.[datasetIndex] as string
        if (clickedData && datasetIndex && label) {
          onBarClick &&
            onBarClick({ value: clickedData as number, label, index: datasetIndex })
        }
      }
    },
    [onBarClick],
  )

  if (isLoading) {
    return (
      <div className={'w-full h-full flex items-center justify-center'}>Loading...</div>
    )
  }

  const data = match(props)
    .with({ type: 'dataset' }, (value) => ({
      labels: value.labels,
      datasets:
        value.datasets.length > 0
          ? value.datasets
          : [
              {
                label: '',
                data: [],
                backgroundColor: undefined,
              },
            ],
    }))
    .otherwise((value: PropsWithData) => ({
      labels: value.data.map((v) => v.label ?? dayjs(v.date).format('MM-DD')),
      datasets: [
        {
          label: 'Value',
          data: yKey ? value.data.map((v) => v[yKey]) : value.data.map((v) => v.value),
          fill: true,
          datalabels: {
            anchor: 'end',
            align: 'start',
            color: '#FFF',
            font: {
              size: 12,
              weight: 'bold',
              fontFamily: 'Spartan',
            } as const,
            padding: {
              top: 32,
            },
          } as const,
          backgroundColor: (context: ScriptableContext<'bar'>): Color | undefined => {
            const chart = context.chart
            const { ctx, chartArea } = chart

            if (!chartArea) return

            return gradients[gradient](ctx, chartArea) as Color
          },
        },
      ],
    }))

  return (
    <div className={'w-full h-full relative'}>
      {isPreviousData && (
        <div className={'absolute top-0 left-0 w-full h-full bg-white opacity-50'} />
      )}
      <Bar
        ref={chartRef}
        options={{
          ...options,
          ...customOptions,
          onHover: (e) => {
            if (onBarClick && e.native && e.native.target)
              (e.native.target as HTMLElement).style.cursor = 'pointer'
          },
        }}
        onClick={handleClick}
        data={data}
      />
    </div>
  )
}

export default BarChart
